import jwtDecode from 'jwt-decode'
import authCrypto from './authCrypto'
import xhr from '../../../store/xhr'

function getHashParams() {
  return location.hash
    .slice(2)
    .split('&')
    .map(p => p.split('='))
    .reduce((obj, [key, value]) => ({ ...obj, [key]: value }), {})
}

function getQueryParams() {
  return location.search
    .slice(1)
    .split('&')
    .map(p => p.split('='))
    .reduce((obj, [key, value]) => ({ ...obj, [key]: value }), {})
}

function isRedirectFromImplicitFlow() {
  return getHashParams().access_token
}

function isRedirectFromAuthCodeFlow() {
  return getQueryParams().code
}

function queryStringify(params) {
  return Object.entries(params).reduce(
    (acc, [key, value], i) =>
      `${acc}${i === 0 ? '' : '&'}${encodeURIComponent(
        key
      )}=${encodeURIComponent(value)}`,
    ''
  )
}

// Identity Provider Specific Functions
function generateAuthUrl({ urlBase, params }) {
  return `${urlBase}?${queryStringify(params)}`
}

async function handleImplicitRedirect(
  authPersist,
  onAuthorisedRedirect,
  onRedirectError,
  next
) {
  try {
    const { id_token, access_token } = getHashParams()
    const idDecoded = jwtDecode(id_token)
    const persistedState = authPersist.getAuthValues()

    authPersist.removeAuthValues()

    const isValidNonce = await authCrypto.verifyHash(
      persistedState.secret,
      idDecoded.nonce
    )

    if (isValidNonce && onAuthorisedRedirect) {
      onAuthorisedRedirect(
        { id: id_token, access: access_token },
        persistedState
      )
      next('/login/auth')
    }
  } catch (e) {
    authPersist.removeAuthValues()
    if (onRedirectError) {
      onRedirectError(e)
    }
  }
}

async function handleAuthCodeRedirect(
  authPersist,
  onAuthorisedRedirect,
  onRedirectError,
  next,
  authProviderConfig
) {
  try {
    const persistedState = authPersist.getAuthValues()
    const authProvider = authProviderConfig[persistedState.authProvider]
    const data = queryStringify({
      grant_type: 'authorization_code',
      client_id: authProvider.params.client_id,
      code: persistedState.code,
      code_verifier: persistedState.secret,
      redirect_uri: `${location.origin}/`
    })

    authPersist.removeAuthValues()

    const authResponse = await xhr.post(authProvider.urls.token, data, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
    })

    let { id_token, refresh_token, access_token } = authResponse.data

    if (onAuthorisedRedirect) {
      onAuthorisedRedirect(
        { id: id_token, refresh: refresh_token, access: access_token },
        persistedState
      )
    }
    next('/login/auth')
  } catch (e) {
    authPersist.removeAuthValues()
    if (onRedirectError) {
      onRedirectError(e)
    }
  }
}

function createAuthRouteInterceptor(
  authPersist,
  onAuthorisedRedirect,
  onRedirectError,
  authProviderConfig
) {
  return (to, from, next) => {
    if (isRedirectFromImplicitFlow()) {
      handleImplicitRedirect(
        authPersist,
        onAuthorisedRedirect,
        onRedirectError,
        next
      )
    } else if (isRedirectFromAuthCodeFlow()) {
      /*
      This is required as the hash routing fails when we redirect from the auth code flow with a query string.
      So we store the code and refresh the page to the base url.
      */
      const { code } = getQueryParams()
      authPersist._persist('code', code)
      window.location.href = `${location.origin}/`
      return
    } else if (authPersist._get('code')) {
      handleAuthCodeRedirect(
        authPersist,
        onAuthorisedRedirect,
        onRedirectError,
        next,
        authProviderConfig
      )
      return
    }
    authPersist.removeAuthValues()

    next()
  }
}

export { createAuthRouteInterceptor, generateAuthUrl }
